import React from 'react'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { RecommendationsContainer } from '../../../interaxDesignSystemComponents/RecommendationsContainer'
import { ButtonSecondaryUpgraded } from '../../../interaxDesignSystemComponents'
import { SectionHeader } from './cards'
import { formatFeedbackText } from '../../feedbackGenerator/helpers/textFormatter'
import type { DraftJsContent } from 'pure-js/libs/Common'
import { draftJsContentToHtml, isDraftJsContent } from '../../feedbackGenerator/helpers/draftJsToHtml'
import { GamerRoutes } from '../../../config/routes/routeEnums'

export interface TryThisAtWorkComponentProps {
  tryThisNextTime?: string | DraftJsContent
  tryThisAtWork?: string | DraftJsContent
  className?: string
}

/**
 * TryThisAtWorkComponent - Displays formatted "Try This At Work" feedback text
 * Shows formatted feedback text from latest session feedback with markdown-like formatting support
 * Uses draftJsContentToHtml to correctly convert DraftJS content to HTML
 */
export const TryThisAtWorkComponent: React.FC<TryThisAtWorkComponentProps> = ({
  tryThisNextTime,
  tryThisAtWork,
  className = ''
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleMoreClick = () => {
    navigate(`${GamerRoutes.MY_PROFILE}#profile-try-this-at-work`)
  }

  // Format the text using formatFeedbackText helper
  const formattedText = React.useMemo(() => {
    // If tryThisAtWork is provided, use it (converting DraftJS content correctly)
    if (tryThisAtWork) {
      if (isDraftJsContent(tryThisAtWork)) {
        // Use draftJsContentToHtml to convert all blocks correctly
        return draftJsContentToHtml(tryThisAtWork)
      }
      if (typeof tryThisAtWork === 'string') {
        return formatFeedbackText(tryThisAtWork)
      }
    }

    // Fallback to tryThisNextTime for backward compatibility
    if (!tryThisNextTime) {
      return ''
    }
    if (isDraftJsContent(tryThisNextTime)) {
      return draftJsContentToHtml(tryThisNextTime)
    }
    if (typeof tryThisNextTime === 'string') {
      return formatFeedbackText(tryThisNextTime)
    }
    return ''
  }, [tryThisNextTime, tryThisAtWork])

  return (
    <RecommendationsContainer className={className}>
      {/* Header */}
      <SectionHeader
        title={t('gamer.dashboard.tryThisAtWork.title', 'Try This At Work')}
        subtitle={t('gamer.dashboard.tryThisAtWork.subtitle', 'Recommendations for improvement in your daily work.')}
      />

      {/* Formatted Text Content in Glass Box */}
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '24px',
          width: '100%',
          background: `linear-gradient(144.63deg, rgba(46, 79, 95, 0.15) 0%, rgba(46, 79, 95, 0.08) 100%), ${INTERAX_COLORS.heritageBlue40}`,
          border: `1px solid rgba(46, 79, 95, 0.2)`,
          boxShadow: `0px 12px 24px rgba(0, 0, 0, 0.25), inset 0px 1px 0px rgba(46, 79, 95, 0.2)`,
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          position: 'relative',
          minHeight: '120px',
          maxHeight: '500px',
          overflowX: 'hidden',
          overflowY: 'auto',
          // Hide scrollbar but keep scrolling functional
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
          '&::-webkit-scrollbar': {
            display: 'none' // Chrome, Safari, Opera
          }
        }}
      >
        {formattedText ? (
          <Box
            sx={{
              ...INTERAX_TYPOGRAPHY.body3,
              color: INTERAX_COLORS.typographyFont,
              lineHeight: 1.6,
              width: '100%',
              '& p': {
                marginBottom: '8px',
                '&:last-child': {
                  marginBottom: 0
                }
              },
              '& ol, & ul': {
                marginLeft: '20px',
                marginBottom: '8px',
                '& li': {
                  marginBottom: '4px'
                }
              },
              '& strong': {
                fontWeight: 600
              }
            }}
            dangerouslySetInnerHTML={{ __html: formattedText }}
          />
        ) : (
          <Box
            sx={{
              ...INTERAX_TYPOGRAPHY.body3,
              color: INTERAX_COLORS.typographyFont,
              width: '100%'
            }}
          >
            {t('gamer.dashboard.tryThisAtWork.empty', 'No feedback available yet.')}
          </Box>
        )}
      </Box>

      {/* More Button - Bottom Right - only when feedback is available */}
      {formattedText && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 'auto',
            paddingTop: '8px'
          }}
        >
          <ButtonSecondaryUpgraded variant="gold" onClick={handleMoreClick}>
            {t('gamer.dashboard.more', 'More')}
          </ButtonSecondaryUpgraded>
        </Box>
      )}
    </RecommendationsContainer>
  )
}

export default TryThisAtWorkComponent
